import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useCreateUniversity } from "@src/presentation/hooks/universities"
import { ToastService } from "@src/shared/services/toast.service"
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Input,
    Textarea,
    Switch,
    Divider
} from "@heroui/react"
import {
    ChevronLeft,
    Building2
} from "lucide-react"

export default function CreateUniversityPage() {
    const navigate = useNavigate()
    const [formData, setFormData] = useState({
        name: "",
        code: "",
        description: "",
        address: "",
        website: "",
        is_active: true,
    })

    const [errors, setErrors] = useState<Record<string, string>>({})

    const { mutate: createUniversity, isPending } = useCreateUniversity({
        onSuccess: () => {
            ToastService.success("University created successfully")
            navigate("/universities")
        },
        onError: (error: Error) => {
            ToastService.error(error.message || "Failed to create university")
        }
    })

    const validateForm = () => {
        const newErrors: Record<string, string> = {}

        if (!formData.name.trim()) {
            newErrors.name = "University name is required"
        }

        if (!formData.code.trim()) {
            newErrors.code = "University code is required"
        } else if (formData.code.length < 2) {
            newErrors.code = "University code must be at least 2 characters"
        }

        if (formData.website && !formData.website.match(/^https?:\/\/.+/)) {
            newErrors.website = "Website must be a valid URL starting with http:// or https://"
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()

        if (!validateForm()) {
            return
        }

        createUniversity({
            name: formData.name.trim(),
            code: formData.code.trim().toUpperCase(),
            description: formData.description.trim() || undefined,
            address: formData.address.trim() || undefined,
            website: formData.website.trim() || undefined,
        })
    }

    const handleInputChange = (field: string, value: string | boolean) => {
        setFormData(prev => ({ ...prev, [field]: value }))
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: "" }))
        }
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-background via-background to-default-50">
            {/* Header */}
            <header className="border-b border-border bg-background/80 backdrop-blur-xl supports-[backdrop-filter]:bg-background/60 sticky top-0 z-20 shadow-none">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row min-h-[56px] sm:h-16 py-2 sm:py-0 items-start sm:items-center justify-between gap-2 sm:gap-0">
                        <div className="flex items-center gap-3">
                            <Button
                                variant="light"
                                size="sm"
                                onPress={() => navigate(-1)}
                                startContent={<ChevronLeft className="h-4 w-4" />}
                                className="hover:scale-105 transition-transform flex-shrink-0"
                            >
                                <span className="hidden sm:inline">Back</span>
                                <span className="sm:hidden">Back</span>
                            </Button>
                            <Divider orientation="vertical" className="h-6 hidden sm:block" />
                            <div className="flex items-center gap-3">
                                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-primary-600 shadow-md">
                                    <Building2 className="h-4 w-4 text-primary-foreground" />
                                </div>
                                <div>
                                    <h1 className="text-xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                                        Create New University
                                    </h1>
                                    <p className="text-xs text-default-500 font-medium">
                                        Add a new university to the system
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="max-w-2xl mx-auto space-y-6">
                    {/* Form */}
                    <Card className="border border-border rounded shadow-none bg-secondary">
                        <CardHeader className="border-b border-border">
                            <h2 className="text-lg font-semibold">University Information</h2>
                        </CardHeader>
                        <CardBody className="space-y-6">
                            <form onSubmit={handleSubmit} className="space-y-6">
                                {/* Name */}
                                <div>
                                    <Input
                                        label="University Name"
                                        placeholder="Enter university name"
                                        value={formData.name}
                                        onValueChange={(value) => handleInputChange("name", value)}
                                        isInvalid={!!errors.name}
                                        errorMessage={errors.name}
                                        startContent={<Building2 className="h-4 w-4 text-default-400" />}
                                        classNames={{
                                            input: "font-medium",
                                            inputWrapper: "rounded border border-border"
                                        }}
                                        isRequired
                                    />
                                </div>

                                {/* Code */}
                                <div>
                                    <Input
                                        label="University Code"
                                        placeholder="e.g., UNILAG, UI, OAU"
                                        value={formData.code}
                                        onValueChange={(value) => handleInputChange("code", value)}
                                        isInvalid={!!errors.code}
                                        errorMessage={errors.code}
                                        description="A short code to identify the university"
                                        classNames={{
                                            input: "font-medium",
                                            inputWrapper: "rounded border border-border"
                                        }}
                                        isRequired
                                    />
                                </div>

                                {/* Description */}
                                <div>
                                    <Textarea
                                        label="Description"
                                        placeholder="Brief description of the university"
                                        value={formData.description}
                                        onValueChange={(value) => handleInputChange("description", value)}
                                        minRows={3}
                                        classNames={{
                                            input: "font-medium",
                                            inputWrapper: "rounded border border-border"
                                        }}
                                    />
                                </div>

                                {/* Address */}
                                <div>
                                    <Textarea
                                        label="Address"
                                        placeholder="University address"
                                        value={formData.address}
                                        onValueChange={(value) => handleInputChange("address", value)}
                                        minRows={2}
                                        classNames={{
                                            input: "font-medium",
                                            inputWrapper: "rounded border border-border"
                                        }}
                                    />
                                </div>

                                {/* Website */}
                                <div>
                                    <Input
                                        label="Website"
                                        placeholder="https://www.university.edu"
                                        value={formData.website}
                                        onValueChange={(value) => handleInputChange("website", value)}
                                        isInvalid={!!errors.website}
                                        errorMessage={errors.website}
                                        type="url"
                                        classNames={{
                                            input: "font-medium",
                                            inputWrapper: "rounded border border-border"
                                        }}
                                    />
                                </div>

                                {/* Active Status */}
                                <div className="flex items-center justify-between p-4 rounded border border-border bg-default-50">
                                    <div>
                                        <p className="font-medium text-foreground">Active Status</p>
                                        <p className="text-sm text-default-500">Whether the university is active and available</p>
                                    </div>
                                    <Switch
                                        isSelected={formData.is_active}
                                        onValueChange={(value) => handleInputChange("is_active", value)}
                                        color="success"
                                    />
                                </div>

                                {/* Actions */}
                                <Divider />
                                <div className="flex flex-col sm:flex-row gap-3 justify-end">
                                    <Button
                                        variant="flat"
                                        onPress={() => navigate("/universities")}
                                        className="rounded"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        color="primary"
                                        isLoading={isPending}
                                        startContent={!isPending && <Building2 className="h-4 w-4" />}
                                        className="rounded"
                                    >
                                        {isPending ? "Creating..." : "Create University"}
                                    </Button>
                                </div>
                            </form>
                        </CardBody>
                    </Card>
                </div>
            </main>
        </div>
    )
}